import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetFancyProfitLossQuery, useGetClientPnlQuery } from '../services/sports/sportsApi';
import Loading from '../components/Loading';

const FancySessionDetailPage = () => {
  const navigate = useNavigate();
  const { matchId, fancyId } = useParams();
  const [layersData, setLayersData] = useState([]);
  const [userId, setUserId] = useState("");
  
  // Get current user's hierarchy level from localStorage
  const getUserData = () => {
    try {
      const userData = localStorage.getItem('user_data');
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      console.error('Error parsing userData from localStorage:', error);
      return null;
    }
  };
  
  const user = getUserData();
  const userLevel = user?.hierarchyLevel || 0;

  // Call API to get fancy profit loss data
  const { data: apiData, isLoading, error: apiError } = useGetFancyProfitLossQuery({
    eventId: matchId,
    fancyId: fancyId
  });

  // Call client PnL API with username from localStorage
  const { data: clientPnlData, isFetching: clientPnlLoading, error: clientPnlError } = useGetClientPnlQuery({
    eventId: matchId,
    userId: userId,
    fancyId: fancyId
  }, {
  });

  useEffect(()=>{
    if (clientPnlData?.data) {
      setLayersData([...layersData, ...clientPnlData.data]);
    }
  }, [clientPnlData]);

  // Get data from API response
  const allData = apiData?.data || [];

  // Group data by user type from layersData
  const groupedData = {
    superAdmin: [],
    miniAdmin: [],
    master: [],
    superAgent: [],
    agent: [],
    client: []
  };

  // Get the latest layer data
  const currentLayerData = layersData;
  
  currentLayerData.forEach(item => {
    const userType = item.userType?.toLowerCase();
    if (userType === 'superadmin') groupedData.superAdmin.push(item);
    else if (userType === 'miniadmin') groupedData.miniAdmin.push(item);
    else if (userType === 'master') groupedData.master.push(item);
    else if (userType === 'super') groupedData.superAgent.push(item);
    else if (userType === 'agent') groupedData.agent.push(item);
    else if (userType === 'client') groupedData.client.push(item);
  });

  // Handle user click
  const handleUserClick = async (userId) => {
   
    setUserId(userId)
    // Fetch new data for clicked user
    // This will be handled by calling the API with userId
  };

  // Calculate totals for each group
  const calculateTotal = (group) => {
    return group.reduce((sum, item) => sum + (item.pnl || 0), 0);
  };
  
  // Get visible cards based on user hierarchy level
  const getVisibleCards = () => {
    const cards = [];
    
    // Admin (level 1) - Show all lower levels (SuperAdmin to Client)
    if (userLevel === 1) {
      cards.push({ key: 'superAdmin', title: 'Super Admin P&L', data: groupedData.superAdmin, hasComm: false });
      cards.push({ key: 'miniAdmin', title: 'Mini Admin P&L', data: groupedData.miniAdmin, hasComm: false });
      cards.push({ key: 'master', title: 'Master P&L', data: groupedData.master, hasComm: true });
      cards.push({ key: 'superAgent', title: 'Super Agent P&L', data: groupedData.superAgent, hasComm: true });
      cards.push({ key: 'agent', title: 'Agent P&L', data: groupedData.agent, hasComm: true });
      cards.push({ key: 'client', title: 'Client P&L', data: groupedData.client, hasComm: true });
    }
    // SuperAdmin (level 2) - Show MiniAdmin to Client (level 3-7)
    else if (userLevel === 2) {
      cards.push({ key: 'miniAdmin', title: 'Mini Admin P&L', data: groupedData.miniAdmin, hasComm: false });
      cards.push({ key: 'master', title: 'Master P&L', data: groupedData.master, hasComm: true });
      cards.push({ key: 'superAgent', title: 'Super Agent P&L', data: groupedData.superAgent, hasComm: true });
      cards.push({ key: 'agent', title: 'Agent P&L', data: groupedData.agent, hasComm: true });
      cards.push({ key: 'client', title: 'Client P&L', data: groupedData.client, hasComm: true });
    }
    // MiniAdmin (level 3) - Show Master to Client (level 4-7)
    else if (userLevel === 3) {
      cards.push({ key: 'master', title: 'Master P&L', data: groupedData.master, hasComm: true });
      cards.push({ key: 'superAgent', title: 'Super Agent P&L', data: groupedData.superAgent, hasComm: true });
      cards.push({ key: 'agent', title: 'Agent P&L', data: groupedData.agent, hasComm: true });
      cards.push({ key: 'client', title: 'Client P&L', data: groupedData.client, hasComm: true });
    }
    // Master (level 4) - Show Super to Client (level 5-7)
    else if (userLevel === 4) {
      cards.push({ key: 'superAgent', title: 'Super Agent P&L', data: groupedData.superAgent, hasComm: true });
      cards.push({ key: 'agent', title: 'Agent P&L', data: groupedData.agent, hasComm: true });
      cards.push({ key: 'client', title: 'Client P&L', data: groupedData.client, hasComm: true });
    }
    // Super (level 5) - Show Agent to Client (level 6-7)
    else if (userLevel === 5) {
      cards.push({ key: 'agent', title: 'Agent P&L', data: groupedData.agent, hasComm: true });
      cards.push({ key: 'client', title: 'Client P&L', data: groupedData.client, hasComm: true });
    }
    // Agent (level 6) - Show only Client (level 7)
    else if (userLevel === 6) {
      cards.push({ key: 'client', title: 'Client P&L', data: groupedData.client, hasComm: true });
    }
    
    return cards;
  };

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = date.toLocaleString('en-US', { month: 'short' });
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    const period = date.getHours() >= 12 ? 'PM' : 'AM';
    return `${day} ${month} ${hours}:${minutes}:${seconds} ${period}`;
  };

  // Render user type card
  const renderUserTypeCard = (title, data, hasComm = true) => {
    const total = calculateTotal(data);
    
    return (
      <div className="bg-white rounded-lg overflow-hidden border border-gray-300 mb-4">
        <div className="bg-[#1e3a3a] text-white p-3">
          <h3 className="font-semibold">{title}</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-800 text-white">
              <tr>
                <th className="p-2 border border-gray-600 text-left">A/C Name</th>
                <th className="p-2 border border-gray-600 text-left">P&L</th>
                {hasComm && <th className="p-2 border border-gray-600 text-left">Comm.</th>}
              </tr>
            </thead>
            <tbody>
              {data.length === 0 ? (
                <tr>
                  <td colSpan={hasComm ? "3" : "2"} className="p-4 text-center text-gray-400">
                    No Data
                  </td>
                </tr>
              ) : (
                data.map((item, index) => {
                  const isLoadingThis = clientPnlLoading && userId === item.userId;
                  return (
                  <tr key={index} className="bg-white border-b border-gray-200">
                    <td className="p-2 border border-gray-300">
                      <span
                        onClick={() => !isLoadingThis && handleUserClick(item.userId)}
                        className={`inline-flex items-center gap-1.5 bg-orange-500 text-white px-2 py-1 text-xs rounded cursor-pointer hover:bg-orange-600 ${isLoadingThis ? 'opacity-70 cursor-wait' : ''}`}
                      >
                        {item.username}
                        {isLoadingThis && (
                          <span className="w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                        )}
                      </span>
                    </td>
                    <td className={`p-2 border border-gray-300 ${item.pnl >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {item.pnl?.toFixed(2)}
                    </td>
                    {hasComm && (
                      <td className="p-2 border border-gray-300">
                        {item.commission?.toFixed(2) || '0.00'}
                      </td>
                    )}
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        {data.length > 0 && (
          <div className="bg-blue-500 p-2">
            <span className="text-white font-semibold">
              Total: <span className={total >= 0 ? 'text-green-200' : 'text-red-200'}>{total.toFixed(2)}</span>
            </span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="w-full bg-gray-100 min-h-screen p-4">
      {/* User Type Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {getVisibleCards().map(card => (
          renderUserTypeCard(card.title, card.data, card.hasComm)
        ))}
      </div>

     

      {/* Event Profit and Loss Table */}
      <div className="bg-white rounded-lg overflow-hidden border border-gray-300">
        <div className="bg-[#1e3a3a] text-white p-3 flex justify-between items-center">
          <h3 className="font-semibold">Event Profit and Loss</h3>
          <button 
            onClick={() => navigate(-1)}
            className="bg-blue-500 text-white px-4 py-1 rounded hover:bg-blue-600"
          >
            Back
          </button>
        </div>
        
        <div className="overflow-x-auto">
          {isLoading ? (
            <div className="flex justify-center items-center py-12">
              <Loading />
            </div>
          ) : apiError ? (
            <div className="p-4 text-red-500 text-center">
              {apiError.message || "Failed to load data"}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-800 text-white">
                <tr>
                  <th className="p-2 border border-gray-600 text-left">username</th>
                  <th className="p-2 border border-gray-600 text-left">Date</th>
                  <th className="p-2 border border-gray-600 text-left">Selection</th>
                  <th className="p-2 border border-gray-600 text-left">Result</th>
                  <th className="p-2 border border-gray-600 text-left">Back/Lay</th>
                  <th className="p-2 border border-gray-600 text-left">Value</th>
                  <th className="p-2 border border-gray-600 text-left">Volume</th>
                  <th className="p-2 border border-gray-600 text-left">Stake</th>
                  <th className="p-2 border border-gray-600 text-left">pnl</th>
                </tr>
              </thead>
              <tbody>
                {allData.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="p-8 text-center text-gray-400">
                      No Data
                    </td>
                  </tr>
                ) : (
                  allData.map((item, index) => {
                    // Set row background based on pnl
                    let rowBgColor;
                    if (item.pnl > 0) {
                      rowBgColor = "rgba(34,197,94,0.18)"; // green tint
                    } else if (item.pnl < 0) {
                      rowBgColor = "rgba(239,68,68,0.18)"; // red tint
                    }

                    return (
                      <tr key={index} style={rowBgColor ? { backgroundColor: rowBgColor } : undefined}>
                        <td className="p-2 border border-gray-300">
                          <span className="text-green-600">{item.username}</span>
                        </td>
                        <td className="p-2 border border-gray-300">{formatDate(item.date)}</td>
                        <td className="p-2 border border-gray-300">{item.fancyName || '-'}</td>
                        <td className="p-2 border border-gray-300">{item.result || '-'}</td>
                        <td className="p-2 border border-gray-300">{item.backLay || '-'}</td>
                        <td className="p-2 border border-gray-300">{item.value || '-'}</td>
                        <td className="p-2 border border-gray-300">{item.volume || '-'}</td>
                        <td className="p-2 border border-gray-300">{item.stake || '-'}</td>
                        <td className={`p-2 border border-gray-300 ${item.pnl >= 0 ? 'text-green-600' : 'text-red-600'} font-semibold`}>
                          {item.pnl?.toFixed(2) || '0.00'}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default FancySessionDetailPage;
